import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Label, Input, StringSelect, Button, cn } from "@tellescope/ui";
import { GripVertical, ChevronDown, Plus, Trash2 } from "lucide-react";
import type {
  Block,
  SectionBlock,
  IframeBlock,
  LeafBlock,
  LeafBlockUpdate,
  BlockType,
  ResponsiveLayout,
  IframeSandbox,
} from "../../../../shared/schema";
import { createDefaultBlock, isLeafBlockType } from "../../../../shared/schema";
import type { VisibilityCondition } from "../../../../shared/schema/visibility";
import type { BlockEditorProps, BlockTypeEditorProps } from "./types";
import { arrayMove } from "./utils";
import { leafBlockIcons, leafBlockLabels, LEAF_BLOCK_TYPES } from "./constants";
import { BlockTypeSelector } from "../BlockTypeSelector";
import { LayoutSection } from "./shared/LayoutSection";
import { VisibilitySection } from "./shared/VisibilitySection";

// Import all block editors
import { TextBlockEditor } from "./editors/TextBlockEditor";
import { HeadingBlockEditor } from "./editors/HeadingBlockEditor";
import { ButtonBlockEditor } from "./editors/ButtonBlockEditor";
import { CardBlockEditor } from "./editors/CardBlockEditor";
import { SpacerBlockEditor } from "./editors/SpacerBlockEditor";
import { DividerBlockEditor } from "./editors/DividerBlockEditor";
import { BannerBlockEditor } from "./editors/BannerBlockEditor";
import { HtmlBlockEditor } from "./editors/HtmlBlockEditor";
import { RichTextBlockEditor } from "./editors/RichTextBlockEditor";
import { PopupBlockEditor } from "./editors/PopupBlockEditor";
import { AppointmentListBlockEditor } from "./editors/AppointmentListBlockEditor";
import { OrderListBlockEditor } from "./editors/OrderListBlockEditor";
import { ChatRoomListBlockEditor } from "./editors/ChatRoomListBlockEditor";
import { FileListBlockEditor } from "./editors/FileListBlockEditor";
import { FormResponseListBlockEditor } from "./editors/FormResponseListBlockEditor";
import { PinnedFormsListBlockEditor } from "./editors/PinnedFormsListBlockEditor";
import { ProfileEditBlockEditor } from "./editors/ProfileEditBlockEditor";
import { ContentListBlockEditor } from "./editors/ContentListBlockEditor";
import { PurchaseListBlockEditor } from "./editors/PurchaseListBlockEditor";
import { StripeSubscriptionsBlockEditor } from "./editors/StripeSubscriptionsBlockEditor";
import { StripeBillingPortalButtonBlockEditor } from "./editors/StripeBillingPortalButtonBlockEditor";
import { FileUploadBlockEditor } from "./editors/FileUploadBlockEditor";
import { AppointmentBookingBlockEditor } from "./editors/AppointmentBookingBlockEditor";
import { VitalDataBlockEditor } from "./editors/VitalDataBlockEditor";

// Re-export types and constants for external use
export type { BlockEditorProps } from "./types";
export { leafBlockIcons, leafBlockLabels, LEAF_BLOCK_TYPES } from "./constants";

export function BlockEditor({ block, onChange }: BlockEditorProps) {
  const [layoutOpen, setLayoutOpen] = useState(false);
  const [visibilityOpen, setVisibilityOpen] = useState(false);

  // Helper to update props while preserving type safety
  const updateProps = <T extends Block>(newProps: Partial<T["props"]>) => {
    onChange({ props: { ...block.props, ...newProps } });
  };

  // Helper to update layout
  const updateLayout = (layout: ResponsiveLayout | undefined) => {
    onChange({ layout });
  };

  // Helper to update visibility
  const updateVisibility = (condition: VisibilityCondition | undefined) => {
    if (condition) {
      onChange({ visibility: { condition } });
    } else {
      onChange({ visibility: undefined });
    }
  };

  // Common props passed to all block editors
  const commonProps = {
    updateLayout,
    updateVisibility,
    layoutOpen,
    setLayoutOpen,
    visibilityOpen,
    setVisibilityOpen,
  };

  switch (block.type) {
    case "text":
      return <TextBlockEditor block={block} updateProps={updateProps} {...commonProps} />;

    case "heading":
      return <HeadingBlockEditor block={block} updateProps={updateProps} {...commonProps} />;

    case "button":
      return <ButtonBlockEditor block={block} updateProps={updateProps} {...commonProps} />;

    case "card":
      return <CardBlockEditor block={block} updateProps={updateProps} {...commonProps} />;

    case "spacer":
      return <SpacerBlockEditor block={block} updateProps={updateProps} {...commonProps} />;

    case "divider":
      return <DividerBlockEditor block={block} updateProps={updateProps} {...commonProps} />;

    case "banner":
      return <BannerBlockEditor block={block} updateProps={updateProps} {...commonProps} />;

    case "html":
      return <HtmlBlockEditor block={block} updateProps={updateProps} {...commonProps} />;

    case "rich-text":
      return <RichTextBlockEditor block={block} updateProps={updateProps} {...commonProps} />;

    case "popup":
      return <PopupBlockEditor block={block} updateProps={updateProps} {...commonProps} />;

    case "appointment-list":
      return <AppointmentListBlockEditor block={block} updateProps={updateProps} {...commonProps} />;

    case "order-list":
      return <OrderListBlockEditor block={block} updateProps={updateProps} {...commonProps} />;

    case "chatroom-list":
      return <ChatRoomListBlockEditor block={block} updateProps={updateProps} {...commonProps} />;

    case "file-list":
      return <FileListBlockEditor block={block} updateProps={updateProps} {...commonProps} />;

    case "form-response-list":
      return <FormResponseListBlockEditor block={block} updateProps={updateProps} {...commonProps} />;

    case "pinned-forms-list":
      return <PinnedFormsListBlockEditor block={block} updateProps={updateProps} {...commonProps} />;

    case "profile-edit":
      return <ProfileEditBlockEditor block={block} updateProps={updateProps} {...commonProps} />;

    case "content-list":
      return <ContentListBlockEditor block={block} updateProps={updateProps} {...commonProps} />;

    case "purchase-list":
      return <PurchaseListBlockEditor block={block} updateProps={updateProps} {...commonProps} />;

    case "stripe-subscriptions":
      return <StripeSubscriptionsBlockEditor block={block} updateProps={updateProps} {...commonProps} />;

    case "stripe-billing-portal-button":
      return <StripeBillingPortalButtonBlockEditor block={block} updateProps={updateProps} {...commonProps} />;

    case "file-upload":
      return <FileUploadBlockEditor block={block} updateProps={updateProps} {...commonProps} />;

    case "appointment-booking":
      return <AppointmentBookingBlockEditor block={block} updateProps={updateProps} {...commonProps} />;

    case "vital-data":
      return <VitalDataBlockEditor block={block} updateProps={updateProps} {...commonProps} />;

    case "section":
      return <SectionBlockEditor block={block} onChange={onChange} {...commonProps} />;

    case "iframe":
      return <IframeBlockEditor block={block} onChange={onChange} {...commonProps} />;

    default: {
      const _exhaustive: never = block;
      return null;
    }
  }
}

// SectionBlockEditor is defined here (not in a separate file) because it needs
// direct access to BlockEditor for rendering child blocks without circular imports
type SectionBlockEditorProps = Omit<BlockTypeEditorProps<SectionBlock>, "updateProps"> & {
  onChange: (updates: Partial<SectionBlock>) => void;
};

function SectionBlockEditor({
  block,
  onChange,
  updateLayout,
  updateVisibility,
  layoutOpen,
  setLayoutOpen,
  visibilityOpen,
  setVisibilityOpen,
}: SectionBlockEditorProps) {
  const { t } = useTranslation();
  const [blockSelectorOpen, setBlockSelectorOpen] = useState(false);
  const [expandedChildId, setExpandedChildId] = useState<string | null>(null);
  const [draggedChildId, setDraggedChildId] = useState<string | null>(null);

  const childBlocks = block.blocks ?? [];

  // Add a new child block
  const handleAddChildBlock = (type: BlockType) => {
    // Only allow leaf block types (not container blocks)
    if (!isLeafBlockType(type)) return;

    const newBlock = createDefaultBlock(type);
    onChange({ blocks: [...childBlocks, newBlock] });
    // Expand the newly added block for editing
    setExpandedChildId(newBlock.id);
  };

  // Update a child block - generic to ensure type safety at call sites
  const handleUpdateChildBlock = <T extends LeafBlock["type"]>(childId: string, updates: LeafBlockUpdate<T>) => {
    const updatedBlocks = childBlocks.map((child) => {
      if (child.id === childId) {
        // Runtime verification: ensure child.type matches updates.type
        if (child.type !== updates.type) {
          console.error(
            `Type mismatch in handleUpdateChildBlock: child is "${child.type}", updates is "${updates.type}"`
          );
          return child;
        }
        return { ...child, ...updates, props: { ...child.props, ...updates.props } };
      }
      return child;
    });
    onChange({ blocks: updatedBlocks });
  };

  // Remove a child block
  const handleRemoveChildBlock = (childId: string) => {
    onChange({ blocks: childBlocks.filter((child) => child.id !== childId) });
    if (expandedChildId === childId) {
      setExpandedChildId(null);
    }
  };

  // Drag handlers for reordering
  const handleDragStart = (childId: string) => {
    setDraggedChildId(childId);
  };

  const handleDragOver = (e: React.DragEvent, targetChildId: string) => {
    e.preventDefault();
    if (!draggedChildId || draggedChildId === targetChildId) return;

    const fromIndex = childBlocks.findIndex((b) => b.id === draggedChildId);
    const toIndex = childBlocks.findIndex((b) => b.id === targetChildId);
    if (fromIndex === -1 || toIndex === -1) return;

    const reorderedBlocks = arrayMove(childBlocks, fromIndex, toIndex);
    onChange({ blocks: reorderedBlocks });
  };

  const handleDragEnd = () => {
    setDraggedChildId(null);
  };

  // Toggle child block expansion
  const handleToggleChild = (childId: string) => {
    setExpandedChildId((prev) => (prev === childId ? null : childId));
  };

  // Filter the block selector to only show leaf block types
  const handleBlockTypeSelect = (type: BlockType) => {
    if (!LEAF_BLOCK_TYPES.includes(type)) return;
    handleAddChildBlock(type);
    setBlockSelectorOpen(false);
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>{t("Title (optional)")}</Label>
        <Input
          value={block.props.title || ""}
          onChange={(e) => onChange({ props: { ...block.props, title: e.target.value || undefined } })}
          placeholder={t("Section title...")}
        />
        <p className="text-xs text-muted-foreground">{t("Optional title for accessibility or visual headers")}</p>
      </div>
      <StringSelect
        label={t("Gap Between Blocks")}
        value={block.props.gap ?? "md"}
        onValueChange={(value) => onChange({ props: { ...block.props, gap: value } })}
        options={[
          { value: "sm", label: t("Small (8px)") },
          { value: "md", label: t("Medium (16px)") },
          { value: "lg", label: t("Large (24px)") },
        ]}
      />

      {/* Child Blocks Section */}
      <div className="pt-2 border-t">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-medium">{t("Child Blocks")}</p>
          <Button variant="ghost" size="sm" onClick={() => setBlockSelectorOpen(true)}>
            <Plus className="h-4 w-4 mr-1" />
            {t("Add")}
          </Button>
        </div>

        {childBlocks.length === 0 ? (
          <div className="text-center py-4 border border-dashed rounded-lg">
            <p className="text-sm text-muted-foreground mb-2">{t("No blocks in this section")}</p>
            <Button variant="outline" size="sm" onClick={() => setBlockSelectorOpen(true)}>
              <Plus className="h-4 w-4 mr-1" />
              {t("Add Block")}
            </Button>
          </div>
        ) : (
          <div className="space-y-1.5">
            {childBlocks.map((child) => {
              const Icon = leafBlockIcons[child.type];
              const isExpanded = expandedChildId === child.id;
              const isDragging = draggedChildId === child.id;

              return (
                <div
                  key={child.id}
                  draggable
                  onDragStart={() => handleDragStart(child.id)}
                  onDragOver={(e) => handleDragOver(e, child.id)}
                  onDragEnd={handleDragEnd}
                  className={cn("rounded border bg-muted/30 transition-opacity", isDragging && "opacity-50")}
                >
                  {/* Child block header */}
                  <div className="flex items-center gap-2 px-2 py-1.5">
                    <div className="cursor-grab active:cursor-grabbing p-0.5 hover:bg-muted rounded">
                      <GripVertical className="h-3.5 w-3.5 text-muted-foreground" />
                    </div>
                    <div
                      className="flex-1 flex items-center gap-2 cursor-pointer"
                      onClick={() => handleToggleChild(child.id)}
                    >
                      <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="flex-1 text-sm font-medium">{leafBlockLabels[child.type]}</span>
                      <ChevronDown
                        className={cn(
                          "h-3.5 w-3.5 text-muted-foreground transition-transform",
                          isExpanded && "rotate-180"
                        )}
                      />
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-5 w-5 p-0 hover:bg-destructive/10 hover:text-destructive"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveChildBlock(child.id);
                      }}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>

                  {/* Expanded child block editor - directly uses BlockEditor */}
                  {isExpanded && (
                    <div className="px-3 pb-3 pt-2 border-t bg-background">
                      <BlockEditor
                        block={child}
                        onChange={({ type: _, ...updates }) =>
                          handleUpdateChildBlock(child.id, { type: child.type, ...updates })
                        }
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Block type selector dialog - filtered to leaf types only */}
      <BlockTypeSelector
        open={blockSelectorOpen}
        onOpenChange={setBlockSelectorOpen}
        onSelect={handleBlockTypeSelect}
      />

      <LayoutSection layout={block.layout} onChange={updateLayout} open={layoutOpen} onOpenChange={setLayoutOpen} />
      <VisibilitySection
        visibility={block.visibility}
        onChange={updateVisibility}
        open={visibilityOpen}
        onOpenChange={setVisibilityOpen}
      />
    </div>
  );
}

// IframeBlockEditor is defined here (not in a separate file) because it needs
// direct access to BlockEditor for rendering fallback blocks without circular imports
type IframeBlockEditorProps = Omit<BlockTypeEditorProps<IframeBlock>, "updateProps"> & {
  onChange: (updates: Partial<IframeBlock>) => void;
};

const IframeBlockEditor = ({
  block,
  onChange,
  updateLayout,
  updateVisibility,
  layoutOpen,
  setLayoutOpen,
  visibilityOpen,
  setVisibilityOpen,
}: IframeBlockEditorProps) => {
  const { t } = useTranslation();
  const [fallbackSelectorOpen, setFallbackSelectorOpen] = useState(false);
  const [expandedFallbackId, setExpandedFallbackId] = useState<string | null>(null);
  const [draggedFallbackId, setDraggedFallbackId] = useState<string | null>(null);

  const fallbackBlocks = block.fallbackBlocks ?? [];
  const sandbox = block.props.sandbox ?? {
    allowScripts: true,
    allowSameOrigin: true,
    allowForms: true,
    allowPopups: false,
  };

  // Get current height as number (convert preset to default 400 if needed)
  const storedHeight = typeof block.props.height === "number" ? block.props.height : 400;
  const [heightInput, setHeightInput] = useState(String(storedHeight));

  // Update props helper
  const updateProps = (updates: Partial<IframeBlock["props"]>) => {
    onChange({ props: { ...block.props, ...updates } });
  };

  // Update sandbox options
  const updateSandbox = (key: keyof IframeSandbox, value: boolean) => {
    updateProps({ sandbox: { ...sandbox, [key]: value } });
  };

  // Handle height input change (allow free typing)
  const handleHeightInputChange = (value: string) => {
    setHeightInput(value);
  };

  // Validate and save height on blur
  const handleHeightBlur = () => {
    const numValue = parseInt(heightInput, 10);
    if (!isNaN(numValue) && numValue >= 100 && numValue <= 2000) {
      updateProps({ height: numValue });
    } else {
      // Reset to stored value if invalid
      setHeightInput(String(storedHeight));
    }
  };

  // Add a new fallback block
  const handleAddFallbackBlock = (type: BlockType) => {
    if (!isLeafBlockType(type)) return;
    const newBlock = createDefaultBlock(type);
    onChange({ fallbackBlocks: [...fallbackBlocks, newBlock] });
    setExpandedFallbackId(newBlock.id);
  };

  // Update a fallback block
  const handleUpdateFallbackBlock = <T extends LeafBlock["type"]>(childId: string, updates: LeafBlockUpdate<T>) => {
    const updatedBlocks = fallbackBlocks.map((child) => {
      if (child.id === childId) {
        if (child.type !== updates.type) {
          console.error(
            `Type mismatch in handleUpdateFallbackBlock: child is "${child.type}", updates is "${updates.type}"`
          );
          return child;
        }
        return { ...child, ...updates, props: { ...child.props, ...updates.props } };
      }
      return child;
    });
    onChange({ fallbackBlocks: updatedBlocks });
  };

  // Remove a fallback block
  const handleRemoveFallbackBlock = (childId: string) => {
    onChange({ fallbackBlocks: fallbackBlocks.filter((child) => child.id !== childId) });
    if (expandedFallbackId === childId) {
      setExpandedFallbackId(null);
    }
  };

  // Drag handlers for reordering
  const handleDragStart = (childId: string) => {
    setDraggedFallbackId(childId);
  };

  const handleDragOver = (e: React.DragEvent, targetChildId: string) => {
    e.preventDefault();
    if (!draggedFallbackId || draggedFallbackId === targetChildId) return;

    const fromIndex = fallbackBlocks.findIndex((b) => b.id === draggedFallbackId);
    const toIndex = fallbackBlocks.findIndex((b) => b.id === targetChildId);
    if (fromIndex === -1 || toIndex === -1) return;

    const reorderedBlocks = arrayMove(fallbackBlocks, fromIndex, toIndex);
    onChange({ fallbackBlocks: reorderedBlocks });
  };

  const handleDragEnd = () => {
    setDraggedFallbackId(null);
  };

  // Toggle fallback block expansion
  const handleToggleFallback = (childId: string) => {
    setExpandedFallbackId((prev) => (prev === childId ? null : childId));
  };

  // Filter the block selector to only show leaf block types (no section or iframe)
  const handleBlockTypeSelect = (type: BlockType) => {
    if (!LEAF_BLOCK_TYPES.includes(type)) return;
    handleAddFallbackBlock(type);
    setFallbackSelectorOpen(false);
  };

  return (
    <div className="space-y-4">
      {/* URL */}
      <div className="space-y-2">
        <Label>{t("URL")} *</Label>
        <Input
          value={block.props.url}
          onChange={(e) => updateProps({ url: e.target.value })}
          placeholder="https://example.com/embed"
        />
        <p className="text-xs text-muted-foreground">
          {t("The URL of the content to embed. Token will be appended as a query parameter.")}
        </p>
      </div>

      {/* Height */}
      <div className="space-y-2">
        <Label>{t("Height (pixels)")}</Label>
        <div className="flex items-center gap-2">
          <Input
            type="number"
            min={100}
            max={2000}
            value={heightInput}
            onChange={(e) => handleHeightInputChange(e.target.value)}
            onBlur={handleHeightBlur}
            className="w-32"
          />
          <span className="text-sm text-muted-foreground">px</span>
        </div>
      </div>

      {/* Title */}
      <div className="space-y-2">
        <Label>{t("Title (accessibility)")}</Label>
        <Input
          value={block.props.title ?? ""}
          onChange={(e) => updateProps({ title: e.target.value || undefined })}
          placeholder={t("Embedded content")}
        />
        <p className="text-xs text-muted-foreground">{t("Used for screen readers and accessibility.")}</p>
      </div>

      {/* Token Settings */}
      <div className="space-y-3 pt-2 border-t">
        <p className="text-sm font-medium">{t("Authentication")}</p>
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="passToken"
            checked={block.props.passToken ?? true}
            onChange={(e) => updateProps({ passToken: e.target.checked })}
            className="h-4 w-4 rounded border-gray-300"
          />
          <Label htmlFor="passToken" className="font-normal">
            {t("Pass authentication token")}
          </Label>
        </div>
        {(block.props.passToken ?? true) && (
          <div className="space-y-2 ml-6">
            <Label>{t("Token parameter name")}</Label>
            <Input
              value={block.props.tokenParam ?? "token"}
              onChange={(e) => updateProps({ tokenParam: e.target.value || "token" })}
              placeholder={t("token")}
              className="w-48"
            />
          </div>
        )}
      </div>

      {/* Sandbox Settings */}
      <div className="space-y-3 pt-2 border-t">
        <p className="text-sm font-medium">{t("Security (Sandbox)")}</p>
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="allowScripts"
              checked={sandbox.allowScripts}
              onChange={(e) => updateSandbox("allowScripts", e.target.checked)}
              className="h-4 w-4 rounded border-gray-300"
            />
            <Label htmlFor="allowScripts" className="font-normal">
              {t("Allow Scripts")}
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="allowSameOrigin"
              checked={sandbox.allowSameOrigin}
              onChange={(e) => updateSandbox("allowSameOrigin", e.target.checked)}
              className="h-4 w-4 rounded border-gray-300"
            />
            <Label htmlFor="allowSameOrigin" className="font-normal">
              {t("Allow Same Origin")}
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="allowForms"
              checked={sandbox.allowForms}
              onChange={(e) => updateSandbox("allowForms", e.target.checked)}
              className="h-4 w-4 rounded border-gray-300"
            />
            <Label htmlFor="allowForms" className="font-normal">
              {t("Allow Forms")}
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="allowPopups"
              checked={sandbox.allowPopups}
              onChange={(e) => updateSandbox("allowPopups", e.target.checked)}
              className="h-4 w-4 rounded border-gray-300"
            />
            <Label htmlFor="allowPopups" className="font-normal">
              {t("Allow Popups")}
            </Label>
          </div>
        </div>
      </div>

      {/* Fallback Blocks Section */}
      <div className="pt-2 border-t">
        <div className="flex items-center justify-between mb-2">
          <div>
            <p className="text-sm font-medium">{t("Fallback Blocks")}</p>
            <p className="text-xs text-muted-foreground font-mono">
              {t("Shown when iframe sends: postMessage({ type: 'iframe-error' })")}
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setFallbackSelectorOpen(true)}>
            <Plus className="h-4 w-4 mr-1" />
            {t("Add")}
          </Button>
        </div>

        {fallbackBlocks.length === 0 ? (
          <div className="text-center py-4 border border-dashed rounded-lg">
            <p className="text-sm text-muted-foreground mb-2">{t("No fallback blocks configured")}</p>
            <Button variant="outline" size="sm" onClick={() => setFallbackSelectorOpen(true)}>
              <Plus className="h-4 w-4 mr-1" />
              {t("Add Fallback Block")}
            </Button>
          </div>
        ) : (
          <div className="space-y-1.5">
            {fallbackBlocks.map((child) => {
              const Icon = leafBlockIcons[child.type];
              const isExpanded = expandedFallbackId === child.id;
              const isDragging = draggedFallbackId === child.id;

              return (
                <div
                  key={child.id}
                  draggable
                  onDragStart={() => handleDragStart(child.id)}
                  onDragOver={(e) => handleDragOver(e, child.id)}
                  onDragEnd={handleDragEnd}
                  className={cn("rounded border bg-muted/30 transition-opacity", isDragging && "opacity-50")}
                >
                  {/* Fallback block header */}
                  <div className="flex items-center gap-2 px-2 py-1.5">
                    <div className="cursor-grab active:cursor-grabbing p-0.5 hover:bg-muted rounded">
                      <GripVertical className="h-3.5 w-3.5 text-muted-foreground" />
                    </div>
                    <div
                      className="flex-1 flex items-center gap-2 cursor-pointer"
                      onClick={() => handleToggleFallback(child.id)}
                    >
                      <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="flex-1 text-sm font-medium">{leafBlockLabels[child.type]}</span>
                      <ChevronDown
                        className={cn(
                          "h-3.5 w-3.5 text-muted-foreground transition-transform",
                          isExpanded && "rotate-180"
                        )}
                      />
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-5 w-5 p-0 hover:bg-destructive/10 hover:text-destructive"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveFallbackBlock(child.id);
                      }}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>

                  {/* Expanded fallback block editor */}
                  {isExpanded && (
                    <div className="px-3 pb-3 pt-2 border-t bg-background">
                      <BlockEditor
                        block={child}
                        onChange={({ type: _, ...updates }) =>
                          handleUpdateFallbackBlock(child.id, { type: child.type, ...updates })
                        }
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Block type selector dialog - filtered to leaf types only */}
      <BlockTypeSelector
        open={fallbackSelectorOpen}
        onOpenChange={setFallbackSelectorOpen}
        onSelect={handleBlockTypeSelect}
      />

      <LayoutSection layout={block.layout} onChange={updateLayout} open={layoutOpen} onOpenChange={setLayoutOpen} />
      <VisibilitySection
        visibility={block.visibility}
        onChange={updateVisibility}
        open={visibilityOpen}
        onOpenChange={setVisibilityOpen}
      />
    </div>
  );
};
